import React, { useState } from 'react';
import { Avatar, Button, Grid, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Add, PointOfSaleOutlined, StorefrontOutlined, TouchAppOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { useCatalogCategories } from './swr/useCatalogCategories';
import { EditCategoryDialog } from './EditCategoryDialog';

const T_PREFIX = 'catalog.emptyState';

const UseCaseRow = ({ icon, titleKey, bodyKey }: { icon: React.ReactNode; titleKey: string; bodyKey: string }) => {
  const { t } = useTranslation();
  return (
    <Grid container flexWrap="nowrap" gap={2} alignItems="flex-start" textAlign="start">
      <Avatar sx={{ bgcolor: 'mb.ground', color: 'primary.main', width: 44, height: 44 }}>{icon}</Avatar>
      <Grid container flexDirection="column">
        <Typography variant="subtitle2">{t(`${T_PREFIX}.${titleKey}`)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`${T_PREFIX}.${bodyKey}`)}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const CatalogEmptyState = () => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const { mutate } = useCatalogCategories();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  return (
    <Grid container flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1} py={6} px={2}>
      <Grid container flexDirection="column" alignItems="center" gap={3} maxWidth="560px">
        <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main' }}>
          <StorefrontOutlined sx={{ fontSize: 40 }} />
        </Avatar>
        <Grid container flexDirection="column" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight={600}>
            {t(`${T_PREFIX}.title`)}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {t(`${T_PREFIX}.whatIsIt`)}
          </Typography>
        </Grid>
        <Grid container flexDirection="column" gap={2.5} my={1}>
          <UseCaseRow icon={<PointOfSaleOutlined />} titleKey="staffUseTitle" bodyKey="staffUseBody" />
          <UseCaseRow icon={<TouchAppOutlined />} titleKey="customerUseTitle" bodyKey="customerUseBody" />
          <UseCaseRow icon={<StorefrontOutlined />} titleKey="whoIsItForTitle" bodyKey="whoIsItForBody" />
        </Grid>
        <Tooltip title={readOnly ? t('noPermissions') : undefined}>
          <span>
            <Button
              disabled={readOnly}
              size="large"
              variant="contained"
              startIcon={<Add />}
              sx={{ textTransform: 'none', px: 4 }}
              onClick={() => setDialogIsOpen(true)}>
              {t(`${T_PREFIX}.addFirstCategory`)}
            </Button>
          </span>
        </Tooltip>
      </Grid>
      {dialogIsOpen && (
        <EditCategoryDialog
          isOpen={dialogIsOpen}
          onClose={() => setDialogIsOpen(false)}
          category={undefined}
          nextSortOrder={0}
          onSaved={() => mutate()}
        />
      )}
    </Grid>
  );
};
